import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '@/api/axiosInstance';

// Types
export interface FathomTranscript {
  id: number;
  platform: string;
  platformId: string;
  meetingTitle: string;
  meetingDate: string | null;
  duration: number | null;
  recordingURL: string | null;
  hostName: string | null;
  hostEmail: string | null;
  attendees: string[] | null;
  meetingInvitees: string[] | null;
  transcript: string | null;
  briefSummary: string | null;
  totalScore: number | null;
  scoredAt: string | null;
  aiFeedback: string | null;
  completedStages: Record<string, string> | null;
  stageTimestamps: Record<string, string> | null;
  currentStage: string | null;
  progressPercentage: number;
  createdAt: string;
  updatedAt: string;
  scoringType: string | null;
  rubricName: string | null;
}

export interface FathomQueueState {
  transcripts: FathomTranscript[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  searchTerm: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  selectedTranscript: any | null;
  detailsLoading: boolean;
}

// API
async function fetchFathomTranscriptsApi(params: {
  page: number;
  pageSize: number;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: string;
}) {
  const queryParams = new URLSearchParams({
    platform: 'fathom',
    page: String(params.page),
    pageSize: String(params.pageSize),
    sortBy: params.sortBy || 'createdAt',
    sortOrder: params.sortOrder || 'DESC',
  });

  if (params.searchTerm) {
    queryParams.set('searchTerm', params.searchTerm);
  }

  const response = await axiosInstance.get(
    `/api/private/transcripts?${queryParams.toString()}`,
  );

  return {
    data: (response.data.transcripts ?? response.data.data ?? []) as FathomTranscript[],
    meta: {
      totalCount: (response.data.totalCount ?? response.data.meta?.totalCount ?? 0) as number,
    },
  };
}

// Slice
const initialState: FathomQueueState = {
  transcripts: [],
  totalCount: 0,
  loading: false,
  error: null,
  searchTerm: '',
  page: 1,
  pageSize: 10,
  sortBy: 'createdAt',
  sortOrder: 'DESC',
  selectedTranscript: null,
  detailsLoading: false,
};

export const fetchFathomTranscripts = createAsyncThunk(
  'fathomQueue/fetchTranscripts',
  async (_, { getState }) => {
    const state = (getState() as any).fathomQueue as FathomQueueState;
    return fetchFathomTranscriptsApi({
      page: state.page,
      pageSize: state.pageSize,
      searchTerm: state.searchTerm || undefined,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
    });
  },
);

export const fetchFathomTranscriptDetails = createAsyncThunk(
  'fathomQueue/fetchTranscriptDetails',
  async (id: string) => {
    const response = await axiosInstance.get(
      `/api/private/call-intelligence/calls/${id}?includeTranscript=true`,
    );
    return response.data;
  },
);

const fathomQueueSlice = createSlice({
  name: 'fathomQueue',
  initialState,
  reducers: {
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 1;
    },
    setSort(state, action: PayloadAction<{ sortBy: string; sortOrder: 'ASC' | 'DESC' }>) {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
      state.page = 1;
    },
    clearSelectedTranscript(state) {
      state.selectedTranscript = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFathomTranscripts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFathomTranscripts.fulfilled, (state, action) => {
        state.loading = false;
        state.transcripts = action.payload.data;
        state.totalCount = action.payload.meta.totalCount;
      })
      .addCase(fetchFathomTranscripts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch Fathom transcripts';
      })
      .addCase(fetchFathomTranscriptDetails.pending, (state) => {
        state.detailsLoading = true;
      })
      .addCase(fetchFathomTranscriptDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedTranscript = action.payload;
      })
      .addCase(fetchFathomTranscriptDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.error.message || 'Failed to fetch transcript details';
      });
  },
});

export const { setSearchTerm, setPage, setPageSize, setSort, clearSelectedTranscript } = fathomQueueSlice.actions;
export default fathomQueueSlice.reducer;
